import { type FC, useId } from "react";
import { type FieldError, get, useFormState } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";

type LabeledSelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  wrapperClassName?: string;
  name: string;
  label: string;
  postLabel?: React.ReactNode;
  required?: boolean;
};

// NOTE: use FormField + Listbox instead of this component
// TODO: remove once it is replaced in VideoTracksForm
export const LabeledSelect: FC<LabeledSelectProps> = ({
  id: idProp,
  className,
  wrapperClassName,
  name,
  label,
  postLabel,
  required = false,
  disabled = false,
  ...props
}) => {
  const randomId = useId();
  const { errors } = useFormState({ name });
  const id = idProp || `${randomId}-${name}`;
  const errorId = `${id}-error`;

  const error = get(errors, name) as FieldError | undefined;
  const errorMessage = typeof error?.message === "string" ? error.message : undefined;
  const invalid = !!error;
  const showError = invalid && !disabled && !!errorMessage;

  const labelNode = (
    <label
      className={twMerge(disabled ? "text-black-tint-70" : "text-black-tint-20", "body-s")}
      htmlFor={id}
    >
      {label}
    </label>
  );

  return (
    <div className={twMerge("flex flex-col", wrapperClassName)}>
      <div className="mb-1 flex items-center justify-between gap-2">
        {postLabel ? (
          <div className="flex items-center gap-2">
            {labelNode}
            {postLabel}
          </div>
        ) : (
          labelNode
        )}
        {!required && (
          <div
            className={twMerge(disabled ? "text-black-tint-70" : "text-black-tint-55", "body-xs")}
          >
            {t("common.optional")}
          </div>
        )}
      </div>
      <select
        aria-describedby={showError ? errorId : undefined}
        aria-invalid={invalid}
        className={twMerge(
          "rounded border border-black-tint-90 px-1.5 py-1 font-normal aria-invalid:border-red",
          className,
        )}
        disabled={disabled}
        id={id}
        name={name}
        {...props}
      />
      {showError ? (
        <div className="body-xs mt-1 text-red" id={errorId}>
          {errorMessage}
        </div>
      ) : null}
    </div>
  );
};
